import React from "react";
import LawFirmRatesCalculator from "@/components/blog/LawFirmRatesCalculator";
import LegalMarketingTechCheck from "@/components/blog/LegalMarketingTechCheck";
import { Link } from "@tanstack/react-router";
import SeoFitCheck from "@/components/blog/SeoFitCheck";
import AdSpendCalculator from "@/components/blog/AdSpendCalculator";
import RankingChangeChecker from "@/components/blog/RankingChangeChecker";
import GeoPriorityChecker from "@/components/blog/GeoPriorityChecker";
import GeoProposalScorecard from "@/components/blog/GeoProposalScorecard";
import CostPerSignedCaseCalculator from "@/components/blog/CostPerSignedCaseCalculator";
import SearchIntentDecoder from "@/components/blog/SearchIntentDecoder";
import IntakeReadinessCheck from "@/components/blog/IntakeReadinessCheck";
import LeadToClientCalculator from "@/components/blog/LeadToClientCalculator";
import PlainLanguageChecker from "@/components/blog/PlainLanguageChecker";
import CostPerLeadComparison from "@/components/blog/CostPerLeadComparison";
import SeoMistakesCheck from "@/components/blog/SeoMistakesCheck";
import SeoProposalScorecard from "@/components/blog/SeoProposalScorecard";
import ReportMetricsCheck from "@/components/blog/ReportMetricsCheck";
import SeoBreakEvenCalculator from "@/components/blog/SeoBreakEvenCalculator";
import SchemaPicker from "@/components/blog/SchemaPicker";
import ContentRefreshCheck from "@/components/blog/ContentRefreshCheck";
import LawFirmSeoTipsTracker from "@/components/blog/LawFirmSeoTipsTracker";
import KeywordPriorityScorer from "@/components/blog/KeywordPriorityScorer";
import SeoPlanBuilder from "@/components/blog/SeoPlanBuilder";
import SchedulerPicker from "@/components/blog/SchedulerPicker";
import GoogleBusinessProfileCheck from "@/components/blog/GoogleBusinessProfileCheck";

/**
 * Renders a markdown-like article body in the site's blog styling.
 * Supports: ## / ###, paragraphs, **bold**, [text](url), - and 1. lists,
 * pipe tables, [[FIGURE ...]], [[COMPONENT Name]] and <!-- comments --> (dropped).
 */

const COMPONENTS: Record<string, React.ComponentType> = { LawFirmRatesCalculator, SchedulerPicker, LegalMarketingTechCheck, SeoFitCheck, AdSpendCalculator, RankingChangeChecker, GeoPriorityChecker, GeoProposalScorecard, CostPerSignedCaseCalculator, SearchIntentDecoder, IntakeReadinessCheck, LeadToClientCalculator, PlainLanguageChecker, CostPerLeadComparison, SeoMistakesCheck, SeoProposalScorecard, ReportMetricsCheck, SeoBreakEvenCalculator, SchemaPicker, ContentRefreshCheck, LawFirmSeoTipsTracker, KeywordPriorityScorer, SeoPlanBuilder, GoogleBusinessProfileCheck };

const FIGURE_SIZES: Record<string, [number, number]> = {
  "hours-to-cash-leakage.svg": [1200, 520],
  "law-firm-weekly-posting-rotation.svg": [1200, 470],
  "bar-compliant-social-post-checklist.svg": [1200, 700],
  "law-firm-tracking-stack.svg": [1200, 560],
  "first-next-never-roadmap.svg": [1200, 660],
  "four-layer-seo-stack.svg": [1200, 620],
  "keyword-priority-matrix.svg": [1200, 700],
  "ai-search-what-helps.svg": [1200, 600],
  "small-vs-multi-office-plans.svg": [1200, 620],
  "law-firm-seo-tips-roadmap.svg": [1200, 560],
  "practice-page-eeat-anatomy.svg": [1200, 860],
  "sub-practice-page-structure.svg": [1200, 620],
  "city-page-decision.svg": [1200, 600],
  "stop-vs-start-instead.svg": [1200, 640],
  "pi-seo-real-work-vs-padding.svg": [1200, 700],
  "seo-proposal-must-haves.svg": [1200, 780],
  "local-market-competition.svg": [1200, 640],
  "ymyl-trust-bar.svg": [1200, 640],
  "vanity-vs-business-metrics.svg": [1200, 640],
  "law-firm-seo-google-results-anatomy.svg": [1200, 1180],
  "law-firm-seo-six-parts.svg": [1200, 780],
  "law-firm-seo-ai-query-fan-out.svg": [1200, 700],
  "law-firm-seo-commodity-vs-expert-content.svg": [1200, 720],
  "law-firm-geo-query-fan-out.svg": [1200, 560],
  "geo-timeline-2023-2026.svg": [1200, 460],
  "seo-vs-geo-results-comparison.svg": [1200, 640],
  "geo-strategy-four-stages.svg": [1200, 440],
  "ai-referral-to-signed-case-path.svg": [1200, 380],
  "search-intent-stages.svg": [1200, 640],
  "search-to-intake-journey.svg": [1200, 560],
  "next-step-by-page-type.svg": [1200, 620],
  "practice-page-trust-signals.svg": [1200, 800],
  "organic-lead-measurement-funnel.svg": [1200, 660],
  "seo-three-jobs.svg": [1200, 540],
  "four-step-lead-journey.svg": [1200, 480],
  "jargon-vs-client-language.svg": [1200, 640],
  "local-results-layout.svg": [1200, 780],
  "seo-vs-ppc-over-time.svg": [1200, 600],
  "ai-search-to-citation-path.svg": [1200, 460],
  "law-firm-entity-footprint.svg": [1200, 600],
  "lead-to-signed-case-intake.svg": [1200, 470],
  "law-firm-marketing-kpi-ladder.svg": [1200, 620],
  "google-business-profile-roles-for-law-firms.svg": [1200, 560],
  "local-3-pack-ranking-factors.svg": [1200, 420],
};

export function slugifyHeading(h: string) {
  return h.toLowerCase().replace(/[^a-z0-9\s-]/g, "").trim().replace(/\s+/g, "-");
}

export function extractH2s(body: string) {
  return body.split("\n").filter((l) => l.startsWith("## ")).map((l) => ({ h: l.slice(3).trim() }));
}

const linkClass =
  "text-[var(--gold-deep)] font-medium underline decoration-[var(--gold)] decoration-2 underline-offset-4 transition-colors";

export function inline(text: string, keyBase: string, extended = false): React.ReactNode[] {
  const out: React.ReactNode[] = [];
  const re = extended ? /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)|\*([^*]+)\*/g : /\*\*([^*]+)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
  let cursor = 0;
  let m: RegExpExecArray | null;
  let k = 0;
  while ((m = re.exec(text))) {
    if (m.index > cursor) out.push(text.slice(cursor, m.index));
    if (m[1] !== undefined) {
      out.push(<strong key={`${keyBase}-b${k++}`} className="font-semibold text-foreground">{inline(m[1], `${keyBase}-b${k}`, extended)}</strong>);
    } else if (m[4] !== undefined) {
      out.push(<em key={`${keyBase}-i${k++}`}>{inline(m[4], `${keyBase}-i${k}`, extended)}</em>);
    } else {
      const href = m[3];
      out.push(
        /^https?:\/\//.test(href) ? (
          <a key={`${keyBase}-l${k++}`} href={href} target="_blank" rel="noopener" className={linkClass}>{m[2]}</a>
        ) : (
          <Link key={`${keyBase}-l${k++}`} to={href} className={linkClass}>{m[2]}</Link>
        ),
      );
    }
    cursor = m.index + m[0].length;
  }
  if (cursor < text.length) out.push(text.slice(cursor));
  return out;
}

const attr = (s: string, name: string) => new RegExp(`${name}="([^"]*)"`).exec(s)?.[1] ?? "";
const cells = (row: string) => row.trim().replace(/^\|/, "").replace(/\|$/, "").split("|").map((c) => c.trim());

export function ArticleBody({ body, afterFirstSection, extendedMarkdown = false, plainTableBody = false, templateBlocks, componentOverrides }: { body: string; afterFirstSection?: React.ReactNode; extendedMarkdown?: boolean; plainTableBody?: boolean; templateBlocks?: Record<string, React.ReactNode>; componentOverrides?: Record<string, React.ReactNode> }) {
  const lines = body.replace(/<!--[\s\S]*?-->/g, "").split("\n");
  const blocks: React.ReactNode[] = [];
  let i = 0;
  let k = 0;
  let h2Count = 0;
  const p = "text-xl text-foreground/85 leading-relaxed";
  const renderInline = (text: string, key: string) => inline(text, key, extendedMarkdown);

  while (i < lines.length) {
    const line = lines[i].trimEnd();
    if (!line.trim()) { i++; continue; }
    const key = `blk-${k++}`;

    if (extendedMarkdown && line.startsWith(">")) {
      const quote: string[] = [];
      while (i < lines.length && lines[i].startsWith(">")) quote.push(lines[i++].replace(/^> ?/, ""));
      blocks.push(<blockquote key={key} className="space-y-4 rounded-lg border-l-4 border-border bg-secondary/50 p-6">{quote.filter((q) => q.trim()).map((q, qi) => <p key={qi} className="text-lg leading-relaxed text-foreground/85">{renderInline(q, `${key}-${qi}`)}</p>)}</blockquote>);
      continue;
    }
    if (extendedMarkdown && line.trim() === "---") {
      blocks.push(<hr key={key} className="border-border" />);
      i++; continue;
    }
    if (extendedMarkdown && /^\*Source:.*\*$/.test(line)) {
      blocks.push(<p key={key} className="text-sm leading-relaxed text-muted-foreground">{renderInline(line, key)}</p>);
      i++; continue;
    }

    if (line.startsWith("## ")) {
      const h = line.slice(3).trim();
      h2Count++;
      if (h2Count === 2 && afterFirstSection) blocks.push(<div key={`${key}-toc`} className="lg:hidden">{afterFirstSection}</div>);
      blocks.push(<h2 key={key} id={slugifyHeading(h)} className="scroll-mt-28 pt-6 text-3xl font-semibold text-foreground">{h}</h2>);
      i++; continue;
    }
    if (line.startsWith("### ")) {
      const h = line.slice(4).trim();
      blocks.push(<h3 key={key} id={slugifyHeading(h)} className="scroll-mt-28 pt-2 text-2xl font-semibold text-foreground">{h}</h3>);
      i++; continue;
    }
    if (line.startsWith("[[IMAGE")) {
      blocks.push(<figure key={key} className="overflow-hidden rounded-lg border border-border bg-card"><img src={attr(line, "src")} alt={attr(line, "alt")} width={Number(attr(line, "width")) || 1200} height={Number(attr(line, "height")) || 800} loading="lazy" decoding="async" className="h-auto w-full" /></figure>);
      i++; continue;
    }
    if (line.startsWith("[[FIGURE")) {
      const src = attr(line, "src");
      const [w, h] = FIGURE_SIZES[src.split("/").pop() ?? ""] ?? [1200, 800];
      blocks.push(
        <figure key={key} className="overflow-hidden rounded-lg border border-border bg-card">
          <img src={src} alt={attr(line, "alt")} width={w} height={h} loading="lazy" decoding="async" className="h-auto w-full" />
          <figcaption className="border-t border-border bg-secondary/40 px-4 py-3 text-sm text-muted-foreground">{attr(line, "caption")}</figcaption>
        </figure>,
      );
      i++; continue;
    }
    const blk = /^\[\[BLOCK (\w+)\]\]/.exec(line);
    if (blk) {
      const node = templateBlocks?.[blk[1]];
      if (node) blocks.push(<div key={key}>{node}</div>);
      i++; continue;
    }
    const comp = /^\[\[COMPONENT (\w+)\]\]/.exec(line);
    if (comp) {
      const override = componentOverrides?.[comp[1]];
      if (override) { blocks.push(<React.Fragment key={key}>{override}</React.Fragment>); i++; continue; }
      const C = COMPONENTS[comp[1]];
      if (C) blocks.push(<C key={key} />);
      i++; continue;
    }
    if (line.startsWith("|")) {
      const rows: string[] = [];
      while (i < lines.length && lines[i].trim().startsWith("|")) rows.push(lines[i++]);
      const headers = cells(rows[0]);
      const body = rows.slice(2).map(cells);
      blocks.push(
        <div key={key} className="overflow-x-auto rounded-lg border border-border">
          <table className="w-full min-w-[640px] border-collapse text-left text-[15px]">
            <thead className="bg-primary text-primary-foreground">
              <tr>{headers.map((h, hi) => <th key={hi} scope="col" className="px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {body.map((row, ri) => (
                <tr key={ri} className={ri % 2 ? "bg-muted/40" : "bg-card"}>
                  {row.map((cell, ci) => (
                    <td key={ci} className={`border-t border-border px-4 py-3 align-top text-foreground/85 ${ci === 0 && !plainTableBody ? "font-semibold text-foreground" : ""}`}>
                      {renderInline(cell, `${key}-${ri}-${ci}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>,
      );
      continue;
    }
    if (/^- /.test(line) || /^\d+\. /.test(line)) {
      const ordered = /^\d+\. /.test(line);
      const items: string[] = [];
      const re = ordered ? /^\d+\. / : /^- /;
      while (i < lines.length && re.test(lines[i])) items.push(lines[i++].replace(re, "").trim());
      const cls = `space-y-3 pl-6 ${p} ${ordered ? "list-decimal" : "list-disc"} marker:text-[var(--gold-deep)] marker:font-semibold`;
      const lis = items.map((it, ii) => <li key={ii}>{renderInline(it, `${key}-${ii}`)}</li>);
      blocks.push(ordered ? <ol key={key} className={cls}>{lis}</ol> : <ul key={key} className={cls}>{lis}</ul>);
      continue;
    }
    // Paragraph: consecutive non-empty lines; each line becomes its own paragraph (FAQ Q/A pairs).
    const para: string[] = [];
    while (i < lines.length && lines[i].trim() && !/^(#{2,3} |\||- |\d+\. |\[\[)/.test(lines[i])) para.push(lines[i++].trim());
    if (para.length === 1) {
      blocks.push(<p key={key} className={p}>{renderInline(para[0], key)}</p>);
    } else {
      blocks.push(
        <div key={key} className="space-y-2">
          {para.map((t, ti) => <p key={ti} className={p}>{renderInline(t, `${key}-${ti}`)}</p>)}
        </div>,
      );
    }
  }

  return <div className="min-w-0 space-y-6">{blocks}</div>;
}
